import { Controller } from "@hotwired/stimulus";

const NAVIGATION_BUTTON_SCROLL_IDLE_MS = 260;
const NAVIGATION_BUTTON_FADE_OUT_MS = 180;
const NAVIGATION_BUTTON_FADE_IN_MS = 240;
const NAVIGATION_BUTTON_FIXED_MIN_WIDTH = 640;
const NAVIGATION_BUTTON_DESKTOP_WIDTH = 1024;

export default class extends Controller {
  static values = {
    firstFutureSlot: Number,
  };

  static targets = ["container", "slide", "prevButton", "nextButton", "stagingLane"];

  connect() {
    // console.log("🎯 Connecting staffing-slots controller");

    this.panState = null;
    this.suppressClick = false;
    this.manualScrollResetTimeout = null;
    this.navigationButtonScrollIdleTimeout = null;
    this.navigationButtonFadeTimeout = null;
    this.hasInitializedScrollPosition = false;
    this.initialSavedScrollLeft = this.pendingSavedScrollLeft() ?? this.savedScrollLeft();
    this.originalScrollSnapType = this.containerTarget.style.scrollSnapType;
    this.originalScrollBehavior = this.containerTarget.style.scrollBehavior;
    this.boundBeforeCacheHandler = this.handleBeforeCache.bind(this);
    this.setupEventListeners();
    this.updateNavigationButtons();
    this.updateNavigationButtonPosition();
    document.addEventListener("turbo:before-cache", this.boundBeforeCacheHandler);
    this.scheduleNormalizeStagingLanes();

    requestAnimationFrame(() => {
      if (this.restoreSavedScrollPosition(this.initialSavedScrollLeft)) {
        this.clearPendingSavedScrollLeft();
        this.hasInitializedScrollPosition = true;
        this.anchorClientWidth = this.containerTarget.clientWidth;
        this.recordAnchorSlide();
        this.scheduleNormalizeStagingLanes();
        return;
      }
      if (this.shouldScrollToOptimalPosition()) {
        this.scrollToOptimalPosition();
      }
      this.hasInitializedScrollPosition = true;
      this.anchorClientWidth = this.containerTarget.clientWidth;
      this.recordAnchorSlide();
      this.scheduleNormalizeStagingLanes();
    });
  }

  disconnect() {
    // console.log("🔌 Disconnecting staffing-slots controller");

    // Clean up observers and listeners
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    document.removeEventListener("turbo:before-cache", this.boundBeforeCacheHandler);
    cancelAnimationFrame(this.scrollSaveFrameRequest);
    cancelAnimationFrame(this.navigationButtonFrameRequest);
    cancelAnimationFrame(this.normalizeStagingFrameRequest);
    window.clearTimeout(this.navigationButtonScrollIdleTimeout);
    window.clearTimeout(this.navigationButtonFadeTimeout);
    this.resetNavigationButtonPosition();
    this.removeEventListeners();
  }

  stagingLaneTargetConnected() {
    this.scheduleNormalizeStagingLanes();
  }

  stagingLaneTargetDisconnected() {
    this.scheduleNormalizeStagingLanes();
  }

  setupEventListeners() {
    // Scroll event listener for navigation button updates and position saving
    this.boundScrollHandler = this.handleScroll.bind(this);
    this.containerTarget.addEventListener("scroll", this.boundScrollHandler);
    this.boundPointerDownHandler = this.handlePointerDown.bind(this);
    this.boundPointerMoveHandler = this.handlePointerMove.bind(this);
    this.boundPointerUpHandler = this.handlePointerUp.bind(this);
    this.boundClickCaptureHandler = this.handleClickCapture.bind(this);
    this.containerTarget.addEventListener("pointerdown", this.boundPointerDownHandler);
    this.containerTarget.addEventListener("click", this.boundClickCaptureHandler, true);

    // Resize observer for responsive behavior
    this.resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.restoreAnchorSlide();
        this.updateNavigationButtons();
        this.updateNavigationButtonPosition();
        this.normalizeStagingLanes();
      });
    });
    this.resizeObserver.observe(this.containerTarget);

    // The user's place in the plan is the leftmost slide, not a scroll offset.
    // When the pane changes width (the sidebar opening takes 22rem from it),
    // mandatory scroll-snap re-evaluates the offset against the new width and
    // can settle on the NEXT slide. Measured: clamped at the end, 1388 on a
    // 296px pitch, a 1280 -> 928 shrink re-snapped to 1480 and the leftmost
    // slot changed. So the anchor is recorded as an element on every user
    // scroll and restored after any width change.
    this.boundAnchorScrollHandler = this.recordAnchorSlide.bind(this);
    this.containerTarget.addEventListener("scroll", this.boundAnchorScrollHandler, { passive: true });
    this.anchorClientWidth = this.containerTarget.clientWidth;
    this.recordAnchorSlide();

    this.boundWindowScrollHandler = this.handleWindowScroll.bind(this);
    this.boundWindowResizeHandler = this.handleWindowResize.bind(this);
    window.addEventListener("scroll", this.boundWindowScrollHandler, { passive: true });
    window.addEventListener("resize", this.boundWindowResizeHandler);
    this.boundVisualViewportScrollHandler = this.handleWindowScroll.bind(this);
    this.boundVisualViewportResizeHandler = this.handleWindowResize.bind(this);
    window.visualViewport?.addEventListener("scroll", this.boundVisualViewportScrollHandler, { passive: true });
    window.visualViewport?.addEventListener("resize", this.boundVisualViewportResizeHandler);
  }

  removeEventListeners() {
    if (this.boundAnchorScrollHandler && this.hasContainerTarget) {
      this.containerTarget.removeEventListener("scroll", this.boundAnchorScrollHandler);
    }
    if (this.boundScrollHandler && this.hasContainerTarget) {
      this.containerTarget.removeEventListener(
        "scroll",
        this.boundScrollHandler,
      );
    }

    if (this.boundPointerDownHandler && this.hasContainerTarget) {
      this.containerTarget.removeEventListener("pointerdown", this.boundPointerDownHandler);
    }

    if (this.boundClickCaptureHandler && this.hasContainerTarget) {
      this.containerTarget.removeEventListener("click", this.boundClickCaptureHandler, true);
    }

    window.removeEventListener("pointermove", this.boundPointerMoveHandler);
    window.removeEventListener("pointerup", this.boundPointerUpHandler);
    window.removeEventListener("pointercancel", this.boundPointerUpHandler);
    window.removeEventListener("scroll", this.boundWindowScrollHandler);
    window.removeEventListener("resize", this.boundWindowResizeHandler);
    window.visualViewport?.removeEventListener("scroll", this.boundVisualViewportScrollHandler);
    window.visualViewport?.removeEventListener("resize", this.boundVisualViewportResizeHandler);
    window.clearTimeout(this.manualScrollResetTimeout);
    window.clearTimeout(this.navigationButtonScrollIdleTimeout);
    window.clearTimeout(this.navigationButtonFadeTimeout);
    this.setManualScrollMode(false);
    this.setPanDragging(false);
    this.panState = null;
  }

  shouldScrollToOptimalPosition() {
    return this.initialSavedScrollLeft === null;
  }

  handleScroll() {
    if (!this.hasInitializedScrollPosition) {
      this.updateNavigationButtons();
      return;
    }

    this.scheduleScrollPositionSave();
    this.updateNavigationButtons();
  }

  handleWindowScroll() {
    if (!this.shouldUseFixedNavigationButtons()) {
      this.resetNavigationButtonPosition();
      return;
    }

    this.hideNavigationButtonsDuringScroll();
    window.clearTimeout(this.navigationButtonScrollIdleTimeout);
    this.navigationButtonScrollIdleTimeout = window.setTimeout(() => {
      this.navigationButtonScrollIdleTimeout = null;

      if (this.updateNavigationButtonPosition()) {
        this.showNavigationButtonsAfterScroll();
      }
    }, NAVIGATION_BUTTON_SCROLL_IDLE_MS);
  }

  handleWindowResize() {
    this.scheduleNavigationButtonPositionUpdate();
  }

  scheduleNavigationButtonPositionUpdate() {
    cancelAnimationFrame(this.navigationButtonFrameRequest);
    this.navigationButtonFrameRequest = requestAnimationFrame(() => {
      this.navigationButtonFrameRequest = null;
      this.updateNavigationButtonPosition();
    });
  }

  updateNavigationButtonPosition() {
    if (
      !this.hasPrevButtonTarget ||
      !this.hasNextButtonTarget ||
      !this.hasContainerTarget
    )
      return false;

    if (!this.shouldUseFixedNavigationButtons()) {
      this.resetNavigationButtonPosition();
      return false;
    }

    const containerRect = this.containerTarget.getBoundingClientRect();
    const viewport = this.visualViewportBounds();
    const visibleTop = Math.max(containerRect.top, viewport.top);
    const visibleBottom = Math.min(containerRect.bottom, viewport.bottom);

    if (visibleBottom <= visibleTop) {
      this.resetNavigationButtonPosition();
      return false;
    }

    const buttonHeight = Math.max(
      this.prevButtonTarget.offsetHeight,
      this.nextButtonTarget.offsetHeight,
      1,
    );
    const buttonWidth = Math.max(
      this.prevButtonTarget.offsetWidth,
      this.nextButtonTarget.offsetWidth,
      1,
    );
    const halfButtonHeight = buttonHeight / 2;
    const viewportGap = 8;
    const visibleCenter = (visibleTop + visibleBottom) / 2;
    const top = Math.min(
      Math.max(visibleCenter, viewport.top + halfButtonHeight + viewportGap),
      viewport.bottom - halfButtonHeight - viewportGap,
    );
    const previousLeft = Math.max(
      containerRect.left - buttonWidth,
      viewport.left + viewportGap,
    );
    const nextLeft = Math.min(
      containerRect.right,
      viewport.right - buttonWidth - viewportGap,
    );

    this.prevButtonTarget.style.position = "fixed";
    this.prevButtonTarget.style.top = `${top}px`;
    this.prevButtonTarget.style.left = `${previousLeft}px`;
    this.prevButtonTarget.style.right = "auto";
    this.nextButtonTarget.style.position = "fixed";
    this.nextButtonTarget.style.top = `${top}px`;
    this.nextButtonTarget.style.left = `${nextLeft}px`;
    this.nextButtonTarget.style.right = "auto";
    return true;
  }

  shouldUseFixedNavigationButtons() {
    if (window.innerWidth >= NAVIGATION_BUTTON_DESKTOP_WIDTH) return true;
    if (window.innerWidth < NAVIGATION_BUTTON_FIXED_MIN_WIDTH) return false;

    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }

  visualViewportBounds() {
    const viewport = window.visualViewport;
    if (!viewport) {
      return {
        top: 0,
        right: window.innerWidth,
        bottom: window.innerHeight,
        left: 0,
      };
    }

    return {
      top: viewport.offsetTop,
      right: viewport.offsetLeft + viewport.width,
      bottom: viewport.offsetTop + viewport.height,
      left: viewport.offsetLeft,
    };
  }

  resetNavigationButtonPosition() {
    [this.prevButtonTarget, this.nextButtonTarget].forEach((button) => {
      button.style.position = "";
      button.style.top = "";
      button.style.left = "";
      button.style.right = "";
      button.style.opacity = "";
      button.style.pointerEvents = "";
      button.style.transition = "";
    });
  }

  hideNavigationButtonsDuringScroll() {
    if (!this.hasPrevButtonTarget || !this.hasNextButtonTarget) return;

    window.clearTimeout(this.navigationButtonFadeTimeout);

    [this.prevButtonTarget, this.nextButtonTarget].forEach((button) => {
      button.style.transition = `opacity ${NAVIGATION_BUTTON_FADE_OUT_MS}ms ease`;
      button.style.opacity = "0";
      button.style.pointerEvents = "none";
    });
  }

  showNavigationButtonsAfterScroll() {
    if (!this.hasPrevButtonTarget || !this.hasNextButtonTarget) return;

    window.clearTimeout(this.navigationButtonFadeTimeout);

    [this.prevButtonTarget, this.nextButtonTarget].forEach((button) => {
      button.style.transition = `opacity ${NAVIGATION_BUTTON_FADE_IN_MS}ms ease`;
      button.style.opacity = "";
      button.style.pointerEvents = "";
    });

    this.navigationButtonFadeTimeout = window.setTimeout(() => {
      [this.prevButtonTarget, this.nextButtonTarget].forEach((button) => {
        button.style.transition = "";
      });
    }, NAVIGATION_BUTTON_FADE_IN_MS + 40);
  }

  handlePointerDown(event) {
    if (!this.hasContainerTarget) return;
    if (!this.shouldStartMousePan(event)) return;

    this.panState = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startScrollLeft: this.containerTarget.scrollLeft,
      dragging: false,
    };
    this.suppressClick = false;

    window.addEventListener("pointermove", this.boundPointerMoveHandler);
    window.addEventListener("pointerup", this.boundPointerUpHandler);
    window.addEventListener("pointercancel", this.boundPointerUpHandler);
  }

  handlePointerMove(event) {
    if (!this.panState || event.pointerId !== this.panState.pointerId) return;

    const deltaX = event.clientX - this.panState.startClientX;
    if (!this.panState.dragging && Math.abs(deltaX) < 6) return;

    if (!this.panState.dragging) {
      this.panState.dragging = true;
      this.suppressClick = true;
      this.setPanDragging(true);
      this.setManualScrollMode(true);
    }

    event.preventDefault();
    this.containerTarget.scrollLeft = this.panState.startScrollLeft - deltaX;
    this.updateNavigationButtons();
  }

  handlePointerUp(event) {
    if (!this.panState || event.pointerId !== this.panState.pointerId) return;

    window.removeEventListener("pointermove", this.boundPointerMoveHandler);
    window.removeEventListener("pointerup", this.boundPointerUpHandler);
    window.removeEventListener("pointercancel", this.boundPointerUpHandler);
    this.setPanDragging(false);
    this.queueManualScrollReset();

    window.setTimeout(() => {
      this.suppressClick = false;
    }, 0);

    this.panState = null;
  }

  handleClickCapture(event) {
    if (this.isModalNavigationTrigger(event.target)) {
      this.savePendingRestoreScrollPosition();
    }

    if (!this.suppressClick) return;

    event.preventDefault();
    event.stopPropagation();
  }

  recordAnchorSlide() {
    if (!this.hasInitializedScrollPosition) return;
    const container = this.containerTarget;
    // A scroll that arrives with a changed width is the browser re-snapping
    // after a resize, not the user moving: keep the anchor from before it.
    if (container.clientWidth !== this.anchorClientWidth) return;

    const left = container.scrollLeft;
    this.anchorSlide = this.slideTargets.find(slide => slide.offsetLeft + slide.offsetWidth > left + 1) || null;
  }

  restoreAnchorSlide() {
    if (!this.hasInitializedScrollPosition) return;
    const container = this.containerTarget;
    if (container.clientWidth === this.anchorClientWidth) return;

    this.anchorClientWidth = container.clientWidth;
    if (!this.anchorSlide || !this.anchorSlide.isConnected) return;

    const left = Math.min(this.anchorSlide.offsetLeft, container.scrollWidth - container.clientWidth);
    if (Math.abs(container.scrollLeft - left) < 1) return;

    container.scrollTo({ left, behavior: "instant" });
    this.saveScrollPosition();
  }

  scrollToOptimalPosition() {
    // console.log("🔍 Calculating optimal scroll position...");

    if (!this.hasContainerTarget) {
      // console.log("❌ Container target not available");
      return;
    }

    if (!this.firstFutureSlotValue && this.firstFutureSlotValue !== 0) {
      // console.log("❌ No firstFutureSlotValue defined");
      return;
    }

    const container = this.containerTarget;
    const slides = this.slideTargets;

    if (slides.length === 0 || container.clientWidth === 0) return;

    const slideWidth = slides[0].offsetWidth;
    const gap = parseInt(getComputedStyle(container).gap) || 16;
    const slideWithGap = slideWidth + gap;
    const maxSlidesInView = Math.floor(
      (container.clientWidth + gap) / slideWithGap,
    );

    // Simple positioning: if future slot fits in view, start from beginning
    // Otherwise, position future slot with some past context
    let targetIndex = 0;
    if (maxSlidesInView < this.firstFutureSlotValue + 2) {
      const pastSlots = Math.max(0, Math.floor(maxSlidesInView * 0.2));
      targetIndex = Math.max(0, this.firstFutureSlotValue - pastSlots);
    }

    // console.log("📜 Scrolling to optimal position:", targetIndex);

    // Use scrollTo with instant behavior to avoid conflicts with scroll snapping
    container.scrollTo({
      left: targetIndex * slideWithGap,
      behavior: "instant",
    });

    this.saveScrollPosition();
    this.updateNavigationButtons();
  }

  scheduleNormalizeStagingLanes() {
    cancelAnimationFrame(this.normalizeStagingFrameRequest);
    this.normalizeStagingFrameRequest = requestAnimationFrame(() => {
      this.normalizeStagingLanes();
    });
  }

  normalizeStagingLanes() {
    if (!this.hasStagingLaneTarget) return;

    this.stagingLaneTargets.forEach((lane) => {
      lane.style.minHeight = "";
    });

    const maxHeight = this.stagingLaneTargets.reduce((height, lane) => {
      return Math.max(height, Math.ceil(lane.getBoundingClientRect().height));
    }, 0);

    if (!maxHeight) return;

    this.stagingLaneTargets.forEach((lane) => {
      lane.style.minHeight = `${maxHeight}px`;
    });
  }

  scrollNext() {
    if (!this.hasContainerTarget) return;

    const container = this.containerTarget;
    const slideWidth = this.slideTargets[0]?.offsetWidth || 300;
    const gap = parseInt(getComputedStyle(container).gap) || 16;

    container.scrollBy({ left: slideWidth + gap, behavior: "smooth" });
  }

  scrollPrev() {
    if (!this.hasContainerTarget) return;

    const container = this.containerTarget;
    const slideWidth = this.slideTargets[0]?.offsetWidth || 300;
    const gap = parseInt(getComputedStyle(container).gap) || 16;

    container.scrollBy({ left: -(slideWidth + gap), behavior: "smooth" });
  }

  updateNavigationButtons() {
    if (
      !this.hasPrevButtonTarget ||
      !this.hasNextButtonTarget ||
      !this.hasContainerTarget
    )
      return;

    const container = this.containerTarget;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    // Use browser's default disabled state
    this.prevButtonTarget.disabled = container.scrollLeft <= 1;
    this.nextButtonTarget.disabled = container.scrollLeft >= maxScrollLeft - 1;
  }

  // Action methods for navigation buttons
  next() {
    this.scrollNext();
  }

  prev() {
    this.scrollPrev();
  }

  shouldStartMousePan(event) {
    if (event.pointerType !== "mouse") return false;
    if (event.button !== 0) return false;
    if (this.isNestedDragInteraction(event.target)) return false;

    const interactiveTarget = event.target.closest(
      [
        "a",
        "button",
        "input",
        "textarea",
        "select",
        "option",
        "label",
        "summary",
        "[role='button']",
        "[contenteditable='true']",
        ".undraggable",
        ".reorder-handle",
        ".reorder-job-handle",
        ".reorder-person-handle",
      ].join(","),
    );

    return !interactiveTarget;
  }

  isNestedDragInteraction(target) {
    if (!(target instanceof Element)) return false;

    const nestedDndContainer = target.closest('[data-controller~="dnd"]');
    if (!nestedDndContainer || nestedDndContainer === this.containerTarget) return false;

    const draggableItem = target.closest("[data-id]");
    if (!draggableItem || draggableItem.parentElement !== nestedDndContainer) return false;

    const handleClass = nestedDndContainer.dataset.dndHandleValue;
    if (!handleClass) return true;

    return !!target.closest(`.${handleClass}`);
  }

  savedScrollLeft() {
    try {
      const value = window.sessionStorage.getItem(this.scrollStorageKey());
      if (value === null) return null;

      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  saveScrollPosition() {
    if (!this.hasContainerTarget) return;

    try {
      window.sessionStorage.setItem(
        this.scrollStorageKey(),
        String(this.containerTarget.scrollLeft),
      );
    } catch {
      // Ignore session storage failures.
    }
  }

  scheduleScrollPositionSave() {
    cancelAnimationFrame(this.scrollSaveFrameRequest);
    this.scrollSaveFrameRequest = requestAnimationFrame(() => {
      this.scrollSaveFrameRequest = null;
      this.saveScrollPosition();
    });
  }

  restoreSavedScrollPosition(savedScrollLeft = this.savedScrollLeft()) {
    if (!this.hasContainerTarget) return false;
    if (savedScrollLeft === null) return false;

    this.containerTarget.scrollTo({
      left: savedScrollLeft,
      behavior: "instant",
    });
    this.saveScrollPosition();
    this.updateNavigationButtons();
    return true;
  }

  scrollStorageKey() {
    return `staffing-slots-scroll:${window.location.pathname}${window.location.search}`;
  }

  pendingScrollStorageKey() {
    return `staffing-slots-scroll-pending:${window.location.pathname}${window.location.search}`;
  }

  pendingSavedScrollLeft() {
    try {
      const value = window.sessionStorage.getItem(this.pendingScrollStorageKey());
      if (value === null) return null;

      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  clearPendingSavedScrollLeft() {
    try {
      window.sessionStorage.removeItem(this.pendingScrollStorageKey());
    } catch {
      // Ignore session storage failures.
    }
  }

  handleBeforeCache() {
    this.saveScrollPosition();
    this.savePendingRestoreScrollPosition();
    this.resetNavigationButtonPosition();
  }

  savePendingRestoreScrollPosition() {
    try {
      window.sessionStorage.setItem(
        this.pendingScrollStorageKey(),
        String(this.containerTarget.scrollLeft),
      );
    } catch {
      // Ignore session storage failures.
    }
  }

  isModalNavigationTrigger(target) {
    if (!(target instanceof Element)) return false;

    return !!target.closest("a[data-turbo-frame='modal']");
  }

  setPanDragging(active) {
    if (!this.hasContainerTarget) return;

    this.containerTarget.classList.toggle("cursor-grabbing", active);
    document.body.classList.toggle("select-none", active);
  }

  setManualScrollMode(active) {
    if (!this.hasContainerTarget) return;

    this.containerTarget.style.scrollSnapType = active ? "none" : this.originalScrollSnapType;
    this.containerTarget.style.scrollBehavior = active ? "auto" : this.originalScrollBehavior;
  }

  queueManualScrollReset() {
    window.clearTimeout(this.manualScrollResetTimeout);
    this.manualScrollResetTimeout = window.setTimeout(() => {
      this.setManualScrollMode(false);
    }, 150);
  }
}
